import useAsyncEffect from '../useAsyncEffect';
import { useState } from 'react';
import { Stores } from './IndexDb';
import { useInitIndexDb } from './useIndexDb';

let request: IDBOpenDBRequest;
let db: IDBDatabase;
const version = 1;

/**
 * A React hook that reads all records from an IndexedDB table and manages loading state. Returns the data array and a dataLoaded flag that flips true once data is retrieved.
 *
 * @param databaseName - The name of the IndexedDB database.
 * @param tableName - The object store name within the database to fetch records from.
 */
export const useIndexDbTable = <T,>(databaseName: string, tableName: Stores) => {
  const { isIndexedDbReady } = useInitIndexDb(databaseName, request, db, version);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [data, setData] = useState<T[]>([]);

  useAsyncEffect(async () => {
    if (isIndexedDbReady) {
      await getIndexDbData<T>(databaseName, tableName)
        .then(async (result) => {
          if (result && Array.isArray(result)) {
            await setData(result);
            await setDataLoaded(true);
          }
        })
        .catch((error) => console.log(error));
    }
  }, [isIndexedDbReady]);
  return { data, dataLoaded, addIndexDbData };
};
/**
 * Synchronously writes a single record to an IndexedDB object store, either adding it (if it doesn't exist) or replacing it, and resolves with the data on success.
 *
 * @param databaseName - The name of the IndexedDB database.
 * @param storeName - The object store name within the database.
 * @param data - The record object to insert or replace.
 * @param updateIfExists - Whether to use put (replace) if the record already exists, or add (skip) if not.
 */
export function addIndexDbData<T>(
  databaseName: string,
  storeName: string,
  data: T,
  updateIfExists = false
): Promise<T | string | null> {
  return new Promise((resolve) => {
    request = indexedDB.open(databaseName, version);

    request.onsuccess = (evt) => {
      // console.log('request.onsuccess - addData', data);
      // @ts-ignore
      db = evt.target?.result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      if (updateIfExists) store.put(data);
      else store.add(data);
      resolve(data);
    };

    request.onerror = () => {
      const error = request.error?.message;
      if (error) {
        resolve(error);
      } else {
        resolve('Unknown error');
      }
    };
  });
}

/**
 * Synchronously reads all records (or a single record by key) from an IndexedDB object store and resolves with the result.
 *
 * @param databaseName - The name of the IndexedDB database.
 * @param storeName - The object store name within the database.
 * @param byId - Optional record key; if provided, fetches a single record instead of all records.
 */
export function getIndexDbData<T>(
  databaseName: string,
  storeName: Stores,
  byId?: string | number
): Promise<T[] | string | null> {
  return new Promise((resolve) => {
    request = indexedDB.open(databaseName);

    request.onsuccess = (evt) => {
      // console.log('request.onsuccess - getAllData');
      // @ts-ignore
      db = evt.target?.result;
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const res = byId ? store.get(byId) : store.getAll();
      res.onsuccess = () => {
        resolve(res.result);
      };
    };

    request.onerror = () => {
      const error = request.error?.message;
      if (error) {
        resolve(error);
      } else {
        resolve('Unknown error');
      }
    };
  });
}
// const update = () => {}
// const remove = () => {}
